import type { CampaignResponse } from '@/api/campaignPlaner/fetchCampaign';
import { runCampaignOptimisation } from '@/dashboards/CampaignOptimization/api/runCampaignOptimisation';
import { readOptimizerMessages } from '@/dashboards/shared/optimisation/optimiserRun';
import type { DashboardRule } from '@/optimizer';
import type { OptimizerServiceManagerReturn } from '@/optimizer/createOptimizerServiceManager';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { buildOptimisationRules } from './buildOptimisationRules';
import { campaignDurationRatio } from './channelBounds';
import {
    deriveFlags,
    initialSessionState,
    reconcileInputs,
    sessionReducer,
    type ChannelInput,
    type OptimisationInputs,
    type OptimisationRun,
    type SessionFlags,
} from '@/dashboards/shared/optimisation/optimisationSession';
import { withoutColdStartNoise } from '@/dashboards/shared/optimisation/optimiserMessages';
import { toError } from '@/dashboards/shared/optimisation/toError';
import { resolveInitialInputs, type CampaignDefaults, type CampaignSearch } from './resolveInitialInputs';

export type UseOptimizationSessionStateArgs = {
    campaign: CampaignResponse;
    configRules: DashboardRule[];
    optimizer: Pick<OptimizerServiceManagerReturn, 'getDefaultBudget' | 'messages'>;
    optimizerReady: boolean;
    search: CampaignSearch;
    setSearch: (patch: CampaignSearch) => unknown;
    defaults: CampaignDefaults;
    minDate: string;
    maxDate: string;
    canEdit: boolean;
};

export type UseOptimizationSessionStateReturn = SessionFlags & {
    draft: OptimisationInputs | null;
    committed: OptimisationRun | null;
    error: Error | null;
    setBudget: (budget: number | undefined) => void;
    setDates: (startDate: string, endDate: string) => void;
    setChannel: (channel: string, value: ChannelInput) => void;
    resetDraft: () => void;
    dismissError: () => void;
    runOptimization: () => void;
};

const lockedChannelNames = (inputs: OptimisationInputs): string[] =>
    Object.entries(inputs.channels)
        .filter(([, channel]) => channel.locked)
        .map(([name]) => name);

const applySearch = (setSearch: UseOptimizationSessionStateArgs['setSearch'], patch: CampaignSearch) => {
    Promise.resolve(setSearch(patch)).catch(() => undefined);
};

export const useOptimizationSessionState = (args: UseOptimizationSessionStateArgs): UseOptimizationSessionStateReturn => {
    const [state, dispatch] = useReducer(sessionReducer, initialSessionState);

    // Latest-props mirror for async callbacks; assigned during render, read only inside callbacks/effects.
    const latest = useRef(args);
    latest.current = args;
    const stateRef = useRef(state);
    stateRef.current = state;

    const runningRef = useRef(false);
    const generationRef = useRef(0);
    const bootstrappedRef = useRef(false);

    const startRun = useCallback(async (inputs: OptimisationInputs) => {
        if (runningRef.current) return;
        runningRef.current = true;
        const generation = ++generationRef.current;
        dispatch({ type: 'RUN_STARTED', generation });

        const { campaign, configRules, optimizer, setSearch } = latest.current;
        try {
            const rules = buildOptimisationRules({
                configRules,
                campaign,
                startDate: inputs.startDate,
                endDate: inputs.endDate,
                lockedChannels: lockedChannelNames(inputs),
            });
            await readOptimizerMessages(optimizer);
            const result = await runCampaignOptimisation({ inputs, rules });
            const messages = withoutColdStartNoise(await readOptimizerMessages(optimizer), {
                isFirstRun: generation === 1,
            });
            const committedInputs = reconcileInputs(inputs, result);
            dispatch({
                type: 'RUN_SUCCEEDED',
                generation,
                run: { inputs: committedInputs, result, messages, finishedAt: Date.now() },
            });
            applySearch(setSearch, {
                budget: committedInputs.budget,
                optimizationBase: committedInputs.optimizationBase,
                startDate: committedInputs.startDate,
                endDate: committedInputs.endDate,
            });
        } catch (error) {
            // The modal shows a one-line summary; keep the full worker payload (code, traceback) in DevTools.
            console.error('[campaign-optimisation] optimisation run failed', error);
            dispatch({
                type: 'RUN_FAILED',
                generation,
                error: toError(error),
            });
        } finally {
            runningRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!args.optimizerReady || bootstrappedRef.current) return;
        bootstrappedRef.current = true;

        void (async () => {
            try {
                const { campaign, optimizer, search, setSearch, defaults, minDate, maxDate } = latest.current;
                const { inputs, urlPatch } = resolveInitialInputs({ search, campaign, defaults, minDate, maxDate });

                let budget = inputs.budget;
                if (budget === null) {
                    const yearly = await optimizer.getDefaultBudget();
                    budget = Math.round(yearly * campaignDurationRatio(inputs.startDate, inputs.endDate));
                }
                const full: OptimisationInputs = { ...inputs, budget };
                dispatch({ type: 'BOOTSTRAPPED', inputs: full });
                if (urlPatch || inputs.budget === null) {
                    applySearch(setSearch, { ...urlPatch, budget });
                }
                await startRun(full);
            } catch (error) {
                dispatch({ type: 'BOOTSTRAP_FAILED', error: toError(error) });
            }
        })();
    }, [args.optimizerReady, startRun]);

    const runOptimization = useCallback(() => {
        const draft = stateRef.current.draft;
        if (draft) void startRun(draft);
    }, [startRun]);

    const setBudget = useCallback((budget: number | undefined) => dispatch({ type: 'EDIT', patch: { budget: budget ?? 0 } }), []);
    const setDates = useCallback((startDate: string, endDate: string) => dispatch({ type: 'EDIT', patch: { startDate, endDate } }), []);
    const setChannel = useCallback((channel: string, value: ChannelInput) => dispatch({ type: 'SET_CHANNEL', channel, value }), []);
    const resetDraft = useCallback(() => dispatch({ type: 'RESET_DRAFT' }), []);
    const dismissError = useCallback(() => dispatch({ type: 'DISMISS_ERROR' }), []);

    const flags = useMemo(() => deriveFlags(state, args.canEdit), [state, args.canEdit]);

    return useMemo<UseOptimizationSessionStateReturn>(
        () => ({
            ...flags,
            draft: state.draft,
            committed: state.committed,
            error: state.error,
            setBudget,
            setDates,
            setChannel,
            resetDraft,
            dismissError,
            runOptimization,
        }),
        [flags, state.draft, state.committed, state.error, setBudget, setDates, setChannel, resetDraft, dismissError, runOptimization],
    );
};
